<template>
  <div class="text-body text-black-2 flex items-center">
    <template v-if="props.promptTags.length">
      <RfColorTag
        v-for="tag in props.promptTags"
        :key="tag.color"
        pill
        :title="tag.title"
        class="p-0! *:py-1!"
        :backgroundColor="tag.color"
        @clear="emit('removeTag', tag)"
      />
    </template>

    <RfMenu ref="tagsMenu" hideIcon preset="datePicker" :disabled="disabled">
      <template #activator>
        <RfButtonNew
          type="plain"
          size="none"
          class="text-body px-2!"
          text="+ Add tag"
          :disabled="disabled"
        />
      </template>
      <template #menu>
        <div class="flex w-64 flex-col gap-4 p-4">
          <RfTextInput
            v-model.trim="newTag.title"
            density="compact"
            variant="outlined"
            :hideDetails="!!filteredTags.length"
            :persistentHint="!filteredTags.length"
            :counter="filteredTags.length ? null : 15"
            :maxlength="filteredTags.length ? null : 15"
            :class="{ '-mb-4': !filteredTags.length }"
            placeholder="Tag name"
          ></RfTextInput>
          <template v-if="filteredTags.length">
            <div class="flex max-h-28 flex-wrap items-start justify-start gap-2 overflow-auto">
              <RfColorTag
                v-for="tag in filteredTags"
                :key="tag.color"
                class="p-0! *:py-1!"
                :title="tag.title"
                pill
                :disabled="disabled"
                :backgroundColor="tag.color"
                @click="emit('assignTag', tag)"
              >
                <template #append>
                  <button class="flex items-center justify-center">
                    <RfPlusMiniIcon class="stroke-beige-50 h-5! w-5!" />
                  </button>
                </template>
              </RfColorTag>
            </div>
            <div class="bg-background h-px w-full"></div>
            <RouterLink
              :to="`/apps/${appId}/settings/tags`"
              class="text-buttons flex items-center justify-between text-blue-50"
            >
              Open in settings <RfLinkIcon class="h-6! w-6! stroke-blue-50!" />
            </RouterLink>
          </template>
          <template v-else>
            <RfTextInput
              v-model.trim="newTag.description"
              density="compact"
              variant="outlined"
              hideDetails
              data-cy="element-info--name"
              placeholder="A tag for..."
              title="Description (optional)"
            ></RfTextInput>
            <div class="flex flex-col gap-1">
              <div>Color</div>
              <div class="flex flex-wrap gap-1">
                <RfColorTag
                  v-for="color in tagsColors"
                  :key="color"
                  :backgroundColor="color"
                  class="h-8 w-8 shrink-0"
                  :selected="getTagColor(newTag.color) === color"
                  @click="newTag.color = color"
                />
              </div>
            </div>
            <div class="bg-background h-px w-full"></div>
            <div class="flex justify-end">
              <RfButtonNew
                type="plain"
                size="none"
                class="text-buttons inline-flex text-blue-50"
                :disabled="disabled"
                @click="
                  (emit('createTag', newTag),
                  (newTag.title = ''),
                  (newTag.description = ''),
                  (newTag.color = tagsColors[0]),
                  tagsMenu?.hide?.())
                "
              >
                Add new tag
              </RfButtonNew>
            </div>
          </template>
        </div>
      </template>
    </RfMenu>
  </div>
</template>

<script setup>
import RfButtonNew from "@/components/buttons/RfButtonNew.vue";
import RfLinkIcon from "@/components/icons/RfLinkIcon.vue";
import RfPlusMiniIcon from "@/components/icons/RfPlusMiniIcon.vue";
import RfTextInput from "@/components/legacy/RfInputLegacy.vue";
import RfMenu from "@/components/menus/RfMenu.vue";
import RfColorTag from "@/components/tags/RfColorTag.vue";
import { tagsColors, getTagColor } from "@/utils/constants/ColorsConstants";
import { computed, reactive, ref } from "vue";
import { RouterLink } from "vue-router";

const props = defineProps({
  appId: { type: String, default: "" },
  appTags: { type: Array, default: () => [] },
  promptTags: { type: Array, default: () => [] },
  disabled: { type: [Boolean, String], default: true },
});

const tagsMenu = ref();
const emit = defineEmits(["removeTag", "assignTag", "createTag"]);
const newTag = reactive({ title: "", description: "", color: tagsColors[0] });
const filteredTags = computed(() =>
  props.appTags.filter(
    ({ title, id }) =>
      title.toLowerCase().includes(newTag.title.toLowerCase()) &&
      props.promptTags.every(tag => tag.id !== id),
  ),
);
</script>
